import {
  PlayerColor, GamePhase, DiceValue, PlayerState, PlayerConfig,
  GameConfig, TokenState, Move, AIDifficulty, TokenPosition,
  PLAYER_ORDER, TOKENS_PER_PLAYER, PATH_LENGTH,
  PLAYER_START_POSITIONS, PLAYER_HOME_STRETCH_ENTRY,
} from './types';
import { RuleEngine } from './RuleEngine';
import { TurnManager } from './TurnManager';
import { TokenController } from './TokenController';
import { DiceManager } from './DiceManager';

export type GameEventType =
  | 'dice_roll_start' | 'dice_roll_end'
  | 'player_ranked'
  | 'move_start' | 'move_end'
  | 'capture' | 'token_home' | 'token_finished'
  | 'turn_change' | 'game_over'
  | 'extra_turn' | 'three_sixes'
  | 'ai_thinking';

export interface GameEvent {
  type: GameEventType;
  data?: Record<string, unknown>;
}

export type GameEventHandler = (event: GameEvent) => void;

export class GameManager {
  private _phase: GamePhase = GamePhase.MENU;
  private _players: PlayerState[] = [];
  private _ruleEngine: RuleEngine;
  private _turnManager: TurnManager;
  private _tokenController: TokenController;
  private _diceManager: DiceManager;
  private _diceValue: DiceValue | null = null;
  private _eventHandlers: GameEventHandler[] = [];
  private _winner: number | null = null;
  private _rankings: number[] = [];
  private _placementPending = false;

  constructor() {
    this._ruleEngine = new RuleEngine();
    this._turnManager = new TurnManager(this._ruleEngine);
    this._tokenController = new TokenController(this._ruleEngine);
    this._diceManager = new DiceManager();
  }

  get phase(): GamePhase { return this._phase; }
  get players(): PlayerState[] { return this._players; }
  get currentPlayerIndex(): number { return this._turnManager.currentPlayerIndex; }
  get currentPlayer(): PlayerState { return this._players[this._turnManager.currentPlayerIndex]; }
  get diceValue(): DiceValue | null { return this._diceValue; }
  get winner(): number | null { return this._winner; }
  get rankings(): readonly number[] { return this._rankings; }
  get diceManager(): DiceManager { return this._diceManager; }
  get ruleEngine(): RuleEngine { return this._ruleEngine; }

  on(callback: GameEventHandler): void {
    this._eventHandlers.push(callback);
  }

  off(callback: GameEventHandler): void {
    this._eventHandlers = this._eventHandlers.filter(h => h !== callback);
  }

  private emit(type: GameEventType, data?: Record<string, unknown>): void {
    const event: GameEvent = { type, data };
    this._eventHandlers.forEach(h => h(event));
  }

  startGame(config: GameConfig): void {
    this._players = config.players.map(p => ({
      config: p,
      tokens: this._tokenController.createInitialTokens(),
      finishedCount: 0,
      consecutiveSixes: 0,
    }));

    this._diceManager.reset();
    this._turnManager.reset();
    this._winner = null;
    this._rankings = [];
    this._placementPending = false;
    this._diceValue = null;
    this._phase = GamePhase.PLAYING;

    this.emit('turn_change', { playerIndex: 0 });
  }

  async rollDice(): Promise<void> {
    if (this._phase !== GamePhase.PLAYING) return;
    if (this._placementPending) return;
    if (this._diceManager.rolling) return;

    this._phase = GamePhase.ANIMATING;
    this.emit('dice_roll_start');

    const value = await this._diceManager.roll();
    this._diceValue = value;

    this.emit('dice_roll_end', { value });

    const player = this.currentPlayer;
    const hasMoves = this._turnManager.hasValidMoves(
      this.currentPlayerIndex, value, this._players,
    );

    if (!hasMoves) {
      const next = this._turnManager.skipTurn(
        this.currentPlayerIndex, value, player, this._players,
      );
      // Three consecutive sixes forfeits the turn even when no moves were
      // available (otherwise 6 with no move would incorrectly keep extra-turn
      // state). Emit the event so the UI can play the error cue.
      if (this._ruleEngine.isThreeSixes(player.consecutiveSixes)) {
        this.emit('three_sixes', { playerIndex: this.currentPlayerIndex });
        player.consecutiveSixes = 0;
      }
      this._turnManager.setCurrentPlayerIndex(next);
      this._phase = GamePhase.PLAYING;
      this.emit('turn_change', { playerIndex: next });
      return;
    }

    if (player.config.isAI) {
      try {
        const aiIndex = this.currentPlayerIndex;
        const aiTokenIndex = await this.selectAIMove(aiIndex, value);

        if (aiTokenIndex >= 0) {
          await this.executeMove(aiIndex, aiTokenIndex, value);
        } else {
          this._phase = GamePhase.PLAYING;
        }
      } catch (err) {
        console.error('AI move failed — passing the turn:', err);
        this._phase = GamePhase.PLAYING;
        const next = this._turnManager.advanceToNextActive(this.currentPlayerIndex, this._players);
        this._turnManager.setCurrentPlayerIndex(next);
        this.emit('turn_change', { playerIndex: next });
      }
    }
  }

  async executeMove(
    playerIndex: number,
    tokenIndex: number,
    diceValue: DiceValue,
  ): Promise<void> {
    if (this._placementPending) return;
    if (this._phase !== GamePhase.ANIMATING && this._phase !== GamePhase.PLAYING) return;

    try {
      this._phase = GamePhase.ANIMATING;
      this.emit('move_start', { playerIndex, tokenIndex, diceValue });

      const move = this._tokenController.executeMove(
        playerIndex, tokenIndex, diceValue, this._players,
      );

      this.emit('move_end', { move });

      if (move.isCapture) {
        this.emit('capture', { playerIndex, tokenIndex });
      }

      if (move.isFinished) {
        this.emit('token_finished', { playerIndex, tokenIndex });

        // Winner is the mover who just completed all 4 gotis — not the first
        // player in order with 4. This fixes 2nd/3rd win never showing when
        // an earlier player already has 4 (checkWin would return 0 again).
        let winner: number | null = null;
        if (this._players[playerIndex].finishedCount === TOKENS_PER_PLAYER) {
          winner = playerIndex;
        }
        if (winner !== null && !this._rankings.includes(winner)) {
          this._rankings.push(winner);
          const unfinished = this._players.filter((_, i) => !this._rankings.includes(i));

          if (unfinished.length <= 1) {
            // Only 0 or 1 player left — game ends.
            // For 2P: 1st wins → [1st, Loser] immediate final (no CONTINUE)
            // For 3P: 2nd wins → [1st,2nd,Loser] final on 2nd's board
            // For 4P: 3rd wins → [1st,2nd,3rd,Loser] final on 3rd's board
            if (unfinished.length === 1) {
              this._rankings.push(this._players.indexOf(unfinished[0]));
            }
            this._winner = this._rankings[0];
            this._phase = GamePhase.GAME_OVER;
            this.emit('game_over', { winner: this._winner, rankings: [...this._rankings] });
            return;
          }

          // Mid-game finish: more than one player still racing — show placement
          // and let the rest continue (1st in 3P/4P, 2nd in 4P) with CONTINUE.
          this._placementPending = true;
          this.emit('player_ranked', {
            playerIndex: winner,
            rank: this._rankings.length,
          });
          const next = this._turnManager.advanceToNextActive(playerIndex, this._players);
          this._turnManager.setCurrentPlayerIndex(next);
          this._phase = GamePhase.PLAYING;
          this.emit('turn_change', { playerIndex: next });
          return;
        }
      }

      const player = this._players[playerIndex];

      if (diceValue === 6) {
        player.consecutiveSixes++;
      } else {
        player.consecutiveSixes = 0;
      }

      if (this._ruleEngine.isThreeSixes(player.consecutiveSixes)) {
        this.emit('three_sixes', { playerIndex });
        player.consecutiveSixes = 0;
        const next = this._turnManager.advanceToNextActive(playerIndex, this._players);
        this._turnManager.setCurrentPlayerIndex(next);
        this._phase = GamePhase.PLAYING;
        this.emit('turn_change', { playerIndex: next });
        return;
      }

      const extraTurnBySix = this._ruleEngine.hasExtraTurn(diceValue, player.consecutiveSixes);
      const extraTurnByCapture = move.isCapture;
      const extraTurnByFinish = move.isFinished;

      if (extraTurnBySix || extraTurnByCapture || extraTurnByFinish) {
        // Priority for status reason: capture > finish > six
        const reason = extraTurnByCapture ? 'capture' : extraTurnByFinish ? 'finish' : 'six';
        this.emit('extra_turn', { playerIndex, reason });
        this._phase = GamePhase.PLAYING;
      } else {
        const next = this._turnManager.getNextPlayer(
          playerIndex, diceValue, player, move.isCapture, this._players,
        );
        this._turnManager.setCurrentPlayerIndex(next);
        this._phase = GamePhase.PLAYING;
        this.emit('turn_change', { playerIndex: next });
      }
    } finally {
      // Safety net: a thrown move must never deadlock the game in ANIMATING
      // (that would silently stop turns from ever passing again).
      if (this._phase === GamePhase.ANIMATING) {
        console.error('executeMove left phase stuck — unlocking.');
        this._phase = GamePhase.PLAYING;
      }
    }
  }

  getValidMoves(diceValue: DiceValue): number[] {
    return this._ruleEngine.getValidMoves(
      this.currentPlayerIndex, diceValue, this._players,
    );
  }

  private async selectAIMove(playerIndex: number, diceValue: DiceValue): Promise<number> {
    this.emit('ai_thinking', { playerIndex });

    await new Promise(r => setTimeout(r, 500 + Math.random() * 500));

    const moves = this._ruleEngine.getValidMoves(playerIndex, diceValue, this._players);
    if (moves.length === 0) return -1;

    const player = this._players[playerIndex];
    const difficulty = player.config.aiDifficulty || AIDifficulty.NORMAL;

    switch (difficulty) {
      case AIDifficulty.EASY:
        return this.selectEasyMove(playerIndex, diceValue, moves);
      case AIDifficulty.NORMAL:
        return this.selectNormalMove(playerIndex, diceValue, moves);
      case AIDifficulty.HARD:
        return this.selectHardMove(playerIndex, diceValue, moves);
      default:
        return moves[Math.floor(Math.random() * moves.length)];
    }
  }

  private selectEasyMove(
    playerIndex: number, _diceValue: DiceValue, moves: number[],
  ): number {
    return moves[Math.floor(Math.random() * moves.length)];
  }

  private selectNormalMove(
    playerIndex: number, diceValue: DiceValue, moves: number[],
  ): number {
    const player = this._players[playerIndex];
    const scored = moves.map(tokenIndex => {
      let score = 0;
      const token = player.tokens[tokenIndex];

      if (token.state === TokenState.HOME) score += 3;
      if (token.state === TokenState.ON_PATH) {
        const newPos = this._ruleEngine.calculateNewPosition(
          token, diceValue, player.config.color,
        );
        const capture = this._ruleEngine.checkCapture(newPos, player.config.color, this._players);
        if (capture !== null) score += 5;
        if (newPos.state === TokenState.HOME_STRETCH) score += 4;
        if (newPos.state === TokenState.FINISHED) score += 10;
        if (token.state === TokenState.ON_PATH) score += 1;
      }

      return { tokenIndex, score };
    });

    scored.sort((a, b) => b.score - a.score);
    const topScore = scored[0].score;
    const topMoves = scored.filter(m => m.score === topScore);
    return topMoves[Math.floor(Math.random() * topMoves.length)].tokenIndex;
  }

  private selectHardMove(
    playerIndex: number, diceValue: DiceValue, moves: number[],
  ): number {
    const player = this._players[playerIndex];
    const scored = moves.map(tokenIndex => {
      let score = 0;
      const token = player.tokens[tokenIndex];

      if (token.state === TokenState.HOME) {
        const startPos = player.tokens.filter(
          t => t.state === TokenState.ON_PATH && t.pathIndex >= 0 && t.pathIndex < 6,
        );
        if (startPos.length < 2) score += 8;
        else score += 4;
      }

      if (token.state === TokenState.ON_PATH) {
        const newPos = this._ruleEngine.calculateNewPosition(
          token, diceValue, player.config.color,
        );
        const capture = this._ruleEngine.checkCapture(newPos, player.config.color, this._players);
        if (capture !== null) {
          const capturedIdx = this._players.findIndex(p =>
            p.tokens.some(t => t.state === TokenState.ON_PATH && t.pathIndex === newPos.pathIndex),
          );
          if (capturedIdx >= 0 && this._players[capturedIdx].finishedCount < 3) {
            score += 12;
          } else {
            score += 8;
          }
        }

        if (newPos.state === TokenState.HOME_STRETCH) {
          const danger = this.checkStretchDanger(playerIndex, newPos);
          if (!danger) score += 6;
          else score += 3;
        }

        if (newPos.state === TokenState.FINISHED) score += 15;

        const distanceToHome = this.distanceToHome(token, player.config.color);
        if (distanceToHome < 10) score += 2;
      }

      if (token.state === TokenState.HOME_STRETCH) {
        const newPos = this._ruleEngine.calculateNewPosition(
          token, diceValue, player.config.color,
        );
        if (newPos.state === TokenState.FINISHED) score += 15;

        const safeAdvance = this.checkStretchAhead(playerIndex, token, diceValue);
        if (safeAdvance) score += 3;
      }

      return { tokenIndex, score };
    });

    scored.sort((a, b) => b.score - a.score);
    const topScore = scored[0].score;
    const topMoves = scored.filter(m => m.score === topScore);

    if (topMoves.length > 1) {
      const prioritized = topMoves.filter(m => {
        const tok = player.tokens[m.tokenIndex];
        return tok.state === TokenState.HOME;
      });
      if (prioritized.length > 0) {
        return prioritized[Math.floor(Math.random() * prioritized.length)].tokenIndex;
      }
    }

    return topMoves[Math.floor(Math.random() * topMoves.length)].tokenIndex;
  }

  private distanceToHome(token: TokenPosition, color: PlayerColor): number {
    if (token.state !== TokenState.ON_PATH) return 99;
    const start = PLAYER_START_POSITIONS[color];
    const entry = PLAYER_HOME_STRETCH_ENTRY[color];
    const steps = token.pathIndex >= start
      ? token.pathIndex - start
      : PATH_LENGTH - start + token.pathIndex;
    const totalPathSteps = entry >= start
      ? entry - start
      : PATH_LENGTH - start + entry;
    return totalPathSteps - steps;
  }

  private checkStretchDanger(_playerIndex: number, _pos: TokenPosition): boolean {
    return false;
  }

  private checkStretchAhead(_playerIndex: number, _token: TokenPosition, _dice: number): boolean {
    return true;
  }

  setPhase(phase: GamePhase): void {
    this._phase = phase;
  }

  resumeAfterPlacement(): void {
    this._placementPending = false;
  }

  reset(): void {
    this._players = [];
    this._diceManager.reset();
    this._turnManager.reset();
    this._winner = null;
    this._placementPending = false;
    this._diceValue = null;
    this._phase = GamePhase.MENU;
  }
}
